import React, {
    useState,
    useEffect,
    useLayoutEffect,
    useRef,
    useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import {
    PRIORITY_CONFIG,
    hasSupportBannerAccess,
    formatDateTime,
} from './bannerUtils';
import { useBanners } from './BannerContext';

const SEEN_COOKIE = 'wv_seen_banners';
const BODY_LIMIT = 70;

// seenMap: { [bannerId]: lastEditedAt }
// A banner is unread if its ID is absent OR its lastEditedAt has changed.
const getSeenMap = () => {
    try {
        const match = document.cookie
            .split(';')
            .find((c) => c.trim().startsWith(SEEN_COOKIE + '='));
        if (!match) return {};
        const parsed = JSON.parse(
            decodeURIComponent(match.split('=')[1].trim())
        );
        // Accept object format only; discard legacy array format
        return parsed && !Array.isArray(parsed) && typeof parsed === 'object'
            ? parsed
            : {};
    } catch {
        return {};
    }
};

const saveSeenMap = (map) => {
    const expires = new Date();
    expires.setDate(expires.getDate() + 30);
    document.cookie = `${SEEN_COOKIE}=${encodeURIComponent(
        JSON.stringify(map)
    )}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
};

const BannerNotificationDropdown = () => {
    const { banners, loaded, refresh } = useBanners();
    const [open, setOpen] = useState(false);
    const [seenMap, setSeenMap] = useState(() => getSeenMap());
    const [expandedIds, setExpandedIds] = useState(new Set());
    const [panelPos, setPanelPos] = useState(null);
    const bellRef = useRef(null);
    const panelRef = useRef(null);

    // Reload when dropdown opens so count is always fresh
    useEffect(() => {
        if (open) refresh();
    }, [open, refresh]);

    // Mark all loaded banners as seen (record id + lastEditedAt) when dropdown opens
    useEffect(() => {
        if (!open || banners.length === 0) return;
        setSeenMap((prev) => {
            const next = { ...prev };
            banners.forEach((b) => {
                next[b.id] = b.lastEditedAt;
            });
            saveSeenMap(next);
            return next;
        });
    }, [open, banners]);

    // Compute panel position from the bell button's viewport rect
    useLayoutEffect(() => {
        if (!open || !bellRef.current) return;
        const rect = bellRef.current.getBoundingClientRect();
        setPanelPos({
            top: rect.bottom - 12,
            right: window.innerWidth - rect.right,
        });
    }, [open]);

    // Close on outside click — check both bell wrapper and portaled panel
    useEffect(() => {
        const handler = (e) => {
            const inBell =
                bellRef.current && bellRef.current.contains(e.target);
            const inPanel =
                panelRef.current && panelRef.current.contains(e.target);
            if (!inBell && !inPanel) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const toggleExpand = useCallback((id) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    if (!hasSupportBannerAccess()) return null;
    if (loaded && banners.length === 0) return null;

    // Panel shows newest-first; context order (priority) is only for the banner strip
    const panelBanners = [...banners].sort((a, b) => {
        const tA = a.lastEditedAt ? new Date(a.lastEditedAt).getTime() : 0;
        const tB = b.lastEditedAt ? new Date(b.lastEditedAt).getTime() : 0;
        return tB - tA;
    });

    // Unread if not seen at all, or if lastEditedAt changed since last seen
    const unreadCount = banners.filter(
        (b) => !seenMap[b.id] || seenMap[b.id] !== b.lastEditedAt
    ).length;
    const totalCount = banners.length;

    const markAllRead = () => {
        setSeenMap((prev) => {
            const next = { ...prev };
            banners.forEach((b) => {
                next[b.id] = b.lastEditedAt;
            });
            saveSeenMap(next);
            return next;
        });
    };

    const panel =
        open && panelPos
            ? ReactDOM.createPortal(
                  <div
                      ref={panelRef}
                      className='bnd-panel'
                      style={{
                          position: 'fixed',
                          top: panelPos.top,
                          right: panelPos.right,
                          zIndex: 10001,
                      }}
                  >
                      <div className='bnd-panel__header'>
                          <div className='bnd-panel__header-left'>
                              <i className='fa-solid fa-bell bnd-panel__header-icon' />
                              <span className='bnd-panel__title'>
                                  Notifications
                              </span>
                          </div>
                      </div>

                      <div className='bnd-panel__list'>
                          {panelBanners.length === 0 ? (
                              <div className='bnd-panel__empty'>
                                  <i className='fa-solid fa-bell' />
                                  <span>No active banners</span>
                              </div>
                          ) : (
                              panelBanners.map((banner) => {
                                  const cfg =
                                      PRIORITY_CONFIG[banner.priority] ||
                                      PRIORITY_CONFIG.Low;
                                  const isExpanded = expandedIds.has(banner.id);
                                  const bodyLong =
                                      banner.body &&
                                      banner.body.length > BODY_LIMIT;
                                  const bodyText =
                                      bodyLong && !isExpanded
                                          ? banner.body.slice(0, BODY_LIMIT) +
                                            '…'
                                          : banner.body;

                                  return (
                                      <div
                                          key={banner.id}
                                          className='bnd-item'
                                          style={{
                                              background: cfg.bgLight,
                                              borderLeft: `4px solid ${cfg.borderColor}`,
                                          }}
                                      >
                                          <div className='bnd-item__title'>
                                              {banner.title}
                                          </div>
                                          {banner.body && (
                                              <div className='bnd-item__body'>
                                                  {bodyText}
                                                  {bodyLong && (
                                                      <button
                                                          className='bnd-item__toggle'
                                                          onClick={(e) => {
                                                              e.stopPropagation();
                                                              toggleExpand(
                                                                  banner.id
                                                              );
                                                          }}
                                                      >
                                                          {isExpanded
                                                              ? 'Show less'
                                                              : 'Show more'}
                                                      </button>
                                                  )}
                                              </div>
                                          )}
                                          <div className='bnd-item__footer'>
                                              <span
                                                  className='bnd-item__priority-badge'
                                                  style={{
                                                      background: cfg.bgBadge,
                                                      color: cfg.color,
                                                  }}
                                              >
                                                  {banner.priority.toUpperCase()}{' '}
                                                  PRIORITY
                                              </span>
                                              <span className='bnd-item__date'>
                                                  {formatDateTime(
                                                      banner.lastEditedAt
                                                  )}
                                              </span>
                                          </div>
                                      </div>
                                  );
                              })
                          )}
                      </div>
                  </div>,
                  document.body
              )
            : null;

    return (
        <>
            <div
                className='bnd-wrapper align-items-center header-menu mr-2'
                ref={bellRef}
            >
                <button
                    className={`bnd-bell-btn ${
                        open ? 'bnd-bell-btn--active' : ''
                    }`}
                    onClick={() => setOpen((o) => !o)}
                    title='Notifications'
                    aria-label='Notifications'
                >
                    <i className='fa-solid fa-bell bnd-bell-btn__icon' />
                    {unreadCount > 0 && (
                        <span className='bnd-badge'>
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                    )}
                </button>
            </div>
            {panel}
        </>
    );
};

export default BannerNotificationDropdown;
